import type { RefObject } from "react";
import { useEffect, useState } from "react";
import type { HlsVideoElement } from "hls-video-element";
import type { MediaProgress } from "@/react/types/MediaProgress";
import type { MediaTranscoding } from "@/react/types/MediaTranscoding";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { MediaSetting } from "@circle-react/types/MediaSetting";
import { TranscriptNotification } from "@circle-react-uikit/TranscriptsPlayer/TranscriptNotification";
import { TranscriptPopup } from "@circle-react-uikit/TranscriptsPlayer/TranscriptPopup";
import { MediaSettingsOverlay } from "../MediaSettingsOverlay";
import { Player } from "./Player";
import { PlayerTheme } from "./PlayerTheme";
import { usePlaybackPositionRestore } from "./hooks/usePlaybackPositionRestore";
import { usePlayer } from "./hooks/usePlayer";
import { useVersionedChapters } from "./hooks/useVersionedChapters";
import { useVideoSource } from "./hooks/useVideoSource";
import { useVideoTranscript } from "./hooks/useVideoTranscript";
import type { VideoMetadata, VideoVariants } from "./types";

export interface VideoPlayerProps {
  chaptersUrl?: string;
  className?: string;
  contentType: string;
  downloadable?: boolean;
  downloadUrl?: string;
  isVideoAnalyticsEnabled?: boolean;
  mediaTranscoding?: MediaTranscoding;
  mediaTranscriptId?: number;
  onLoadedMetadata?: (metadata: VideoMetadata) => void;
  onPlayerReady?: (api: VideoPlayerApi) => void;
  onProgress?: (progress: MediaProgress) => void;
  onTimeUpdate?: (time: number) => void;
  posterUrl?: string;
  preload?: "none" | "metadata" | "auto";
  previewThumbnailsUrl?: string;
  settingsEnabled?: boolean;
  signedId: string;
  src: string;
  variants?: VideoVariants;
}

export interface VideoPlayerApi {
  seek: (time: number) => void;
  setCustomPlaybackRestoreTime: (time: number) => void;
}

interface RenderPlayerProps {
  fullHeight?: boolean;
  videoRef: RefObject<HlsVideoElement>;
}

export const VideoPlayer = ({
  chaptersUrl,
  className,
  contentType,
  downloadable = false,
  downloadUrl,
  isVideoAnalyticsEnabled,
  mediaTranscoding,
  mediaTranscriptId,
  onProgress,
  onTimeUpdate,
  onLoadedMetadata,
  onPlayerReady,
  posterUrl,
  preload = "metadata",
  previewThumbnailsUrl: sourcePreviewThumbnailsUrl,
  settingsEnabled = true,
  signedId,
  src,
  variants: sourceVariants,
}: VideoPlayerProps) => {
  const { playbackSrc, previewThumbnailsUrl, isHls } = useVideoSource({
    mediaTranscoding,
    signedId,
    src,
    sourceVariants,
    sourcePreviewThumbnailsUrl,
  });

  const {
    mediaTranscript,
    captionsUrl,
    cues,
    shouldDisplayTranscript,
    shouldDisplayNotification,
    updateMediaTranscript,
  } = useVideoTranscript({ mediaTranscriptId });

  const mainPlayer = usePlayer();
  const fullScreenPlayer = usePlayer();
  const [currentTime, setCurrentTime] = useState(0);
  const { versionedChaptersUrl, updateChaptersUrl, chaptersRenderKey } =
    useVersionedChapters(chaptersUrl);

  // A workaround to keep the player position after remount, which happens
  // after modifying chapters and is itself a workaround to avoid time slider
  // artifacts
  const { restorePlaybackPosition, setCustomPlaybackRestoreTime } =
    usePlaybackPositionRestore({
      key: chaptersRenderKey,
      seek: mainPlayer.seek,
      currentTime,
    });

  useEffect(() => {
    onProgress?.({ currentTime, duration: mainPlayer.duration });
  }, [onProgress, currentTime, mainPlayer]);

  useEffect(() => {
    if (!onPlayerReady) return;

    onPlayerReady({ seek: mainPlayer.seek, setCustomPlaybackRestoreTime });
  }, [onPlayerReady, mainPlayer.seek, setCustomPlaybackRestoreTime]);

  const onFullscreenToggle = () => {
    // Stop the main player so its progress callbacks are not executed
    mainPlayer.pause();
    // Update the playback position to sync with the one in the transcript popup
    mainPlayer.seek(currentTime);
  };

  const handleTimeUpdate = (time: number) => {
    onTimeUpdate?.(time);
    setCurrentTime(time);
  };

  const handleChaptersUpdated = (updatedMediaSettings: MediaSetting) => {
    updateChaptersUrl(updatedMediaSettings.chapters_url ?? undefined);
  };

  const handleLoadedMetadata = (metadata: VideoMetadata) => {
    onLoadedMetadata?.(metadata);
    restorePlaybackPosition();
  };

  const renderPlayer = ({
    videoRef,
    fullHeight = false,
  }: RenderPlayerProps) => (
    <PlayerTheme
      key={chaptersRenderKey}
      downloadable={downloadable}
      downloadUrl={downloadUrl}
      fullHeight={fullHeight}
    >
      <Player
        className="max-h-full min-h-full min-w-full max-w-full object-contain object-center"
        src={playbackSrc}
        contentType={contentType}
        captionsUrl={captionsUrl}
        chaptersUrl={versionedChaptersUrl}
        previewThumbnailsUrl={previewThumbnailsUrl}
        preload={preload}
        poster={posterUrl}
        isHls={isHls}
        isVideoAnalyticsEnabled={isVideoAnalyticsEnabled}
        signedId={signedId}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        videoRef={videoRef}
      />
    </PlayerTheme>
  );

  return (
    <div className={classNames("group relative flex flex-col", className)}>
      {renderPlayer(mainPlayer)}
      {settingsEnabled && (
        <MediaSettingsOverlay
          signedId={signedId}
          onChaptersUpdated={handleChaptersUpdated}
          onOpen={() => {
            mainPlayer.pause();
          }}
        />
      )}
      {shouldDisplayTranscript && (
        <TranscriptPopup
          onSeek={mainPlayer.seek}
          renderFullscreenPlayer={() =>
            renderPlayer({
              videoRef: fullScreenPlayer.videoRef,
              fullHeight: true,
            })
          }
          onFullscreenSeek={fullScreenPlayer.seek}
          onFullscreenToggle={onFullscreenToggle}
          cues={cues}
          currentTime={currentTime}
        />
      )}
      {shouldDisplayNotification && (
        <TranscriptNotification
          url={src}
          onNewMediaTranscript={updateMediaTranscript}
          onMediaTranscriptUpdated={updateMediaTranscript}
          mediaTranscript={mediaTranscript}
          isLoading={false}
        />
      )}
    </div>
  );
};
